import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Check, Globe, LayoutTemplate } from "lucide-react";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { AGENCY_TZ, api, formatDate, formatMoney, formatPhone } from "../api";
import { shortDate } from "../ads";
import { ErrorState, LoadingState, MetricCard, PageHeader } from "../components/Ui";
import TrackingSites from "../components/TrackingSites";
import TrackingOverview from "../components/TrackingOverview";
import ConversionFeed from "../components/ConversionFeed";
import MetaConversions from "../components/MetaConversions";
import ConnectionNotice, { type FeedKey } from "../components/ConnectionNotice";
import DateRangePicker, { useUrlDateRange } from "../components/DateRangePicker";
import { rangeApiQuery, rangePhrase, resolveRange, spanDays, todayIn, type PresetKey } from "../date-range";
import {
  ActionForm,
  DataTable,
  RequestView,
  Tabs,
  useMutation,
  type RecordData,
} from "../components/Workflow";

type Tier = "ghl_pages" | "client_site" | null;
type Step = {
  group: string;
  key: string;
  label: string;
  done: boolean;
  detail: string;
  manual?: boolean;
  optional?: boolean;
  /** Had data and it stopped (a lapsed feed), unlike a step nobody has started. */
  stale?: boolean;
  /** Where the step is done, for the people who can do it. */
  link?: { to: string; label: string };
};
type Setup = {
  tier: Tier;
  steps: Step[];
  progress: { done: number; total: number };
  connections?: { ads_accounts: number; ctm_accounts: number; lead_form_key: boolean; ghl_linked: boolean };
};
type Span = { from: string; to: string };
type Attribution = {
  period_days: number;
  period?: Span;
  totals: {
    jobs?: number;
    matched?: number;
    completed?: number;
    /** null when no job export and no won deal covers the period. */
    revenue?: number | null;
    /** 'ghl_won' when the client has no job in the period and revenue is GHL won deal value instead. Never both. */
    revenue_source?: "jobs" | "ghl_won" | null;
    won_deals?: number;
    /** null when the client has no Google Ads account and no spend rows. */
    google_spend?: number | null;
    ads_accounts?: number;
    /** null until Google has reported the client's "NL-AOS Booked" action at least once. */
    google_credited?: number | null;
    cost_per_credited_booking?: number | null;
    credited_status?: "no_action" | "reported" | "not_seen" | "waiting";
    credited_action?: string | null;
    credited_synced_at?: string | null;
    /** Revenue over Google spend on only the days the revenue covers. */
    roas?: number | null;
    /** The spend that ROAS divides by: Google spend on the days the revenue covers. */
    roas_spend?: number | null;
    revenue_covers?: Span | null;
    /** The client's whole job export: first and last booking day. */
    job_export?: { first_day: string; last_day: string } | null;
  };
  /** Campaign spend and Google's calls start on this day; set when the period starts earlier. */
  detail_from?: string | null;
  campaign_spend_span?: Span | null;
  by_channel: RecordData[];
  by_campaign: RecordData[];
  by_location?: RecordData[];
  unmatched_by_how_heard: RecordData[];
  google_ads_calls: RecordData[];
  notes: string[];
};
type Numbers = {
  numbers: RecordData[];
  unmapped_ctm_numbers: RecordData[];
  channels: Record<string, string>;
  locations?: { id: number; market_name: string }[];
};

const TIERS: { value: Exclude<Tier, null>; title: string; body: string; icon: ReactNode }[] = [
  {
    value: "ghl_pages",
    title: "Ads land on GHL pages",
    body: "NOLA controls the page. GHL records the click ID and UTM tags on each form lead, and the funnel header can carry the Google tag and CTM number swap.",
    icon: <LayoutTemplate aria-hidden="true" />,
  },
  {
    value: "client_site",
    title: "Ads land on the client's site",
    body: "No scripts allowed. Forms come through Google Ads lead forms, calls through Google forwarding numbers, and revenue through the client's job export.",
    icon: <Globe aria-hidden="true" />,
  },
];

/** "Jul 20 to Aug 18"; with years when the span crosses one. */
function dayRange(span: Span | { first_day: string; last_day: string }): string {
  const from = "from" in span ? span.from : span.first_day;
  const to = "to" in span ? span.to : span.last_day;
  if (from.slice(0, 4) !== to.slice(0, 4)) return `${formatDate(from)} to ${formatDate(to)}`;
  return from === to ? shortDate(from) : `${shortDate(from)} to ${shortDate(to)}`;
}
// Jobs take a while to book and complete, so Tracking offers longer windows than the Ads pages.
const TRACKING_PRESETS: PresetKey[] = ["30d", "90d", "180d", "365d", "this_week", "last_week", "mtd", "last_month"];
const daysBetween = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / 86400000);
/** "Oct 2" for a moment, on the agency's calendar. */
const easternDay = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: AGENCY_TZ });
const sameSpan = (a?: Span | null, b?: Span | null) => !!a && !!b && a.from === b.from && a.to === b.to;
const roasText = (v: number) => `${v.toFixed(1)}×`;
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Brings a section into view: the upload area from an empty state, the script panel from Website analytics. */
function scrollToId(...ids: string[]) {
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
      return true;
    }
  }
  return false;
}
/**
 * The Setup tab's panels load their own data when the tab opens, so a section
 * asked for from elsewhere (an upload button on Results, a link from Website
 * analytics) appears a moment later: try for a few seconds, then give up quietly.
 */
function scrollWhenReady(...ids: string[]) {
  let tries = 0;
  const attempt = () => {
    if (scrollToId(...ids) || ++tries > 40) return;
    window.setTimeout(attempt, 100);
  };
  window.setTimeout(attempt, 50);
}
// Sections that live on the Setup tab: a link to one of them opens that tab.
const SETUP_ANCHORS = ["tracking-script", "conversion-upload", "conversion-sync", "tracking-numbers", "tracking-leadforms"];

/**
 * An automatic step's state, as a word at the row's end. Manual steps have the
 * checkbox at the start instead, so the two never look alike.
 */
function StepState({ step }: { step: Step }) {
  const state = step.done ? "done" : step.stale ? "stale" : step.optional ? "optional" : "todo";
  const word = { done: "Done", stale: "Stopped", optional: "Optional", todo: "Not yet" }[state];
  return <span className={`tracking-auto-state ${state}`}>{word}</span>;
}

/** The top rows, then one row summing the rest, so a long tail never buries the table. */
function topWithRest(rows: RecordData[], n: number): RecordData[] {
  if (rows.length <= n + 1) return rows;
  const rest = rows.slice(n);
  return [
    ...rows.slice(0, n),
    {
      how_heard: `${rest.length} other answers`,
      jobs: rest.reduce((a, r) => a + Number(r.jobs || 0), 0),
      revenue: Math.round(rest.reduce((a, r) => a + Number(r.revenue || 0), 0) * 100) / 100,
    },
  ];
}

/**
 * A step's detail as the checklist shows it. The job count there is every booked
 * job on file (all uploads), which read as a contradiction beside the period's
 * "5 / 116" and the sheet's row count: name what it counts.
 */
function stepDetail(s: Step): string {
  if (s.key === "jobs_fresh") return s.detail.replace(/^([\d,]+) jobs?, /, (_, n) => `${n} booked ${n === "1" ? "job" : "jobs"} on file, `);
  if (s.key === "matches") return s.detail.replace(/^([\d,]+ matched jobs?)$/, "$1 on file");
  return s.detail;
}

/** What the Credited by Google tile says under its number. */
function creditedDetail(t: Attribution["totals"]): string {
  // The conversion action's own name in Google Ads, in quotes, never the app's name as a subject.
  const name = `"${t.credited_action || "NL-AOS Booked"}"`;
  switch (t.credited_status) {
    case "no_action": return `No ${name} action in Google Ads yet`;
    case "not_seen": return t.credited_synced_at ? `Google synced ${easternDay(t.credited_synced_at)}, no ${name} yet` : `Google reports no ${name} yet`;
    case "waiting": return `Google has not reported ${name} yet`;
    default:
      return t.cost_per_credited_booking != null ? `${formatMoney(t.cost_per_credited_booking)} per credited booking` : "Bookings Google Ads counted itself";
  }
}

function TrackingWorkspace({ clientId, clientName }: { clientId: number; clientName: string }) {
  const { user } = useAuth();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const canEdit = user?.role === "superadmin" || user?.role === "exec";
  const isAdmin = user?.role === "superadmin";
  // Results (what tracking found) and Setup (the one-time work behind it) are two
  // tabs of one page. A link to a setup section opens Setup.
  const hashId = location.hash.slice(1);
  const tab: "results" | "setup" = params.get("tab") === "setup" || (!params.get("tab") && SETUP_ANCHORS.includes(hashId)) ? "setup" : "results";
  const setTab = (next: "results" | "setup") => setParams((current) => {
    const p = new URLSearchParams(current);
    if (next === "setup") p.set("tab", "setup"); else p.delete("tab");
    return p;
  }, { replace: true });
  // Attribution windows run to today (a job booked this morning counts).
  const [range, setRange] = useUrlDateRange(TRACKING_PRESETS, "90d", 730);
  const today = todayIn();
  const phrase = rangePhrase(range, today);
  // Rebuild matches looks back from today far enough to cover the window's first day.
  const matchDays = Math.min(730, Math.max(7, spanDays(resolveRange(range, today, "today").from, today)));
  // The setup summary is small and both tabs read it (which feeds to watch, the tier).
  // Everything else on Setup loads only once that tab is open.
  const setup = useApi<Setup>(`/api/tracking/${clientId}/setup`);
  const attribution = useApi<Attribution>(tab === "results" ? `/api/tracking/${clientId}/attribution?${rangeApiQuery(range, today, "today")}` : null);
  const numbers = useApi<Numbers>(tab === "setup" ? `/api/tracking/${clientId}/numbers` : null);
  const leads = useApi<{ leads: RecordData[] }>(tab === "setup" ? `/api/tracking/${clientId}/leads?limit=50` : null);
  // Each panel reports in its own place, with its own busy state.
  const tierMutation = useMutation();
  const checkMutation = useMutation();
  const matchMutation = useMutation();
  const importMutation = useMutation();
  const numberMutation = useMutation();
  const keyMutation = useMutation();
  const [changingTier, setChangingTier] = useState(false);
  const [allLeads, setAllLeads] = useState(false);
  const [addKey, setAddKey] = useState(0);
  const [confirmChannel, setConfirmChannel] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState<{ key: string; webhook_url: string; note: string } | null>(null);

  // A new period is a new address, which clears the data: keep the last numbers
  // on screen, dimmed, until the new ones land, so the page does not jump.
  const lastAttribution = useRef<Attribution | null>(null);
  if (attribution.data) lastAttribution.current = attribution.data;
  const shown = attribution.data ?? (attribution.loading ? lastAttribution.current : null);
  const attributionBusy = attribution.loading && !!shown;

  // From Website analytics (#tracking-script) and other pages: bring the section
  // into view once the Setup tab's panels have drawn it.
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current || !hashId || tab !== "setup") return;
    scrolled.current = true;
    scrollWhenReady(hashId);
  }, [hashId, tab]);
  /** "Upload a newer export" on Results: open Setup at the upload area. */
  const goToUpload = () => {
    setTab("setup");
    scrollWhenReady("conversion-upload", "conversion-sync");
  };

  const tier = setup.data?.tier ?? null;
  const reloadResults = () => { setup.reload(); attribution.reload(); };

  const setTier = async (value: Tier) => {
    const r = await tierMutation.run(`/api/tracking/${clientId}/tier`, { tier: value }, "PUT");
    if (r) {
      // The one-line summary shows the new tier at once; the reload brings its checklist.
      setup.setData((d) => (d ? { ...d, tier: value } : d));
      setChangingTier(false);
      setup.reload();
    }
  };
  const tick = async (step: Step, done: boolean) => {
    const r = await checkMutation.run(`/api/tracking/${clientId}/manual`, { step: step.key, done }, "PUT");
    if (r && setup.data) {
      // Optional steps never count toward progress (the server leaves them out too).
      setup.setData({
        ...setup.data,
        steps: setup.data.steps.map((s) => (s.key === step.key ? { ...s, done, detail: done ? "Ticked just now" : "" } : s)),
        progress: step.optional ? setup.data.progress : { ...setup.data.progress, done: setup.data.progress.done + (done ? 1 : -1) },
      });
      setup.reload();
    }
  };
  const rebuild = async () => {
    const r = await matchMutation.run<RecordData>(`/api/tracking/${clientId}/match`, { days: matchDays });
    if (r) {
      const noContact = r.jobs - r.jobs_with_contact;
      matchMutation.setNotice(
        `Matched ${r.matched} of ${plural(r.jobs, "job")} in the last ${r.days ?? matchDays} days (${r.matched_by_phone} by phone, ${r.matched_by_email} by email).${noContact ? ` ${plural(noContact, "job")} had no phone or email to match on.` : ""}`,
      );
      reloadResults();
    }
  };
  // A number's channel decides what its past calls credit: rebuild matches quietly, so By channel
  // agrees right away instead of after tonight's run.
  const rematchQuietly = () => {
    void api(`/api/tracking/${clientId}/match`, { method: "POST", body: JSON.stringify({ days: matchDays }) }).then(reloadResults, () => undefined);
  };
  const addNumber = async (data: RecordData) => {
    const r = await numberMutation.run(`/api/tracking/${clientId}/numbers`, data);
    if (r) {
      numberMutation.setNotice("Number saved.");
      // A fresh form, so the next number starts blank.
      setAddKey((k) => k + 1);
      numbers.reload();
      setup.reload();
      rematchQuietly();
    }
  };
  const addUnmapped = async (row: RecordData) => {
    const channel = confirmChannel[`u:${row.tracking_number}`] || row.guessed_channel;
    const r = await numberMutation.run(`/api/tracking/${clientId}/numbers`, {
      phone: row.phone || row.tracking_number, channel, label: row.label || null, location_id: row.location_id ?? null,
    });
    if (r) {
      numberMutation.setNotice(`${formatPhone(row.phone || row.tracking_number)} added. Its calls are credited to that channel from now on.`);
      numbers.reload();
      setup.reload();
      rematchQuietly();
    }
  };
  const removeNumber = async (row: RecordData) => {
    const r = await numberMutation.run(`/api/tracking/numbers/${row.id}`, {}, "DELETE");
    if (r) {
      numberMutation.setNotice(`${row.phone} retired.`);
      numbers.reload();
      setup.reload();
      rematchQuietly();
    }
  };
  const importCtm = async () => {
    const r = await importMutation.run<{ imported: number; already_mapped: number; unreadable: number; accounts: { name?: string; error?: string }[] }>(`/api/tracking/${clientId}/numbers/import-ctm`, {});
    if (r) {
      const errs = r.accounts.filter((a) => a.error);
      importMutation.setNotice(
        `${r.imported ? `Imported ${plural(r.imported, "number")}. Confirm each channel below.` : "Nothing new: every CTM number is already listed."}${r.unreadable ? ` ${plural(r.unreadable, "number")} could not be read as a phone number.` : ""}${errs.length ? ` ${errs.map((a) => `${a.name || "One account"}: ${a.error}`).join(" ")}` : ""}`,
      );
      numbers.reload();
    }
  };
  const confirmNumber = async (row: RecordData) => {
    const r = await numberMutation.run(`/api/tracking/${clientId}/numbers`, {
      phone: row.phone_e164, channel: confirmChannel[String(row.id)] || row.channel_key, label: row.label, campaign: row.campaign, location_id: row.location_id,
    });
    if (r) {
      numberMutation.setNotice("Number confirmed. Its calls are credited to that channel from now on.");
      numbers.reload();
      setup.reload();
      rematchQuietly();
    }
  };
  const keyLive = !!setup.data?.connections?.lead_form_key;
  const generateKey = async () => {
    if (keyLive && !window.confirm("Replace the webhook key? The current key stops working straight away, so Google Ads lead forms deliver nothing until the new key is pasted into Google Ads.")) return;
    const r = await keyMutation.run<{ key: string; webhook_url: string; note: string }>(`/api/tracking/${clientId}/leadform-key`, {});
    if (r) {
      setNewKey(r);
      setup.reload();
    }
  };
  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      keyMutation.setNotice(`${what} copied.`);
    } catch {
      keyMutation.setNotice("Copy failed. Select it and copy it by hand.");
    }
  };

  const steps = setup.data?.steps || [];
  const groups = [...new Set(steps.map((s) => s.group))];
  const conn = setup.data?.connections;
  // The feeds this client's numbers depend on: Google Ads always, CTM and GHL only where linked.
  const feeds: FeedKey[] = ["google_ads", ...(conn?.ctm_accounts ? (["ctm"] as FeedKey[]) : []), ...(tier === "ghl_pages" && conn?.ghl_linked ? (["ghl"] as FeedKey[]) : [])];
  const channels = numbers.data?.channels || {};
  const channelOptions = Object.entries(channels).map(([value, label]) => ({ value, label }));
  const territories = numbers.data?.locations || [];
  const unmapped = numbers.data?.unmapped_ctm_numbers || [];

  const t = shown?.totals;
  const period = shown?.period;
  const wonDeals = t?.revenue_source === "ghl_won";
  // Won deal value is not invoiced work: tag it wherever it stands in for job revenue.
  const wonRevenue = (value: unknown) => (
    <span title="Revenue: GHL won deals, not invoiced jobs">{formatMoney(Number(value || 0))}<em className="tracking-tag">GHL won deals</em></span>
  );
  const covers = t?.revenue_covers ?? null;
  const partial = !!covers && !!period && !sameSpan(covers, period);
  // The job export stopped more than a week before today: say so, in warning tone.
  const exportStops = t?.job_export && period && daysBetween(t.job_export.last_day, period.to) > 7 ? t.job_export : null;
  const noResults = !!t && !t.jobs && !wonDeals && !(shown?.google_ads_calls.length);

  const tierInfo = TIERS.find((o) => o.value === tier);

  // The period and Rebuild matches drive the numbers on Results: they sit in the page header, above the tiles they change.
  const headerActions = tab === "results" && !(noResults && !t?.job_export) ? (
    <div className="detail-header-actions tracking-header-actions">
      <DateRangePicker value={range} onChange={setRange} presets={TRACKING_PRESETS} rollingEnd="today" maxDays={730} label="Period" className="dash-period" />
      {canEdit && (
        <button className="button secondary" disabled={matchMutation.busy || attribution.loading} onClick={() => void rebuild()}>
          {matchMutation.busy ? "Matching…" : "Rebuild matches"}
        </button>
      )}
    </div>
  ) : undefined;

  return (
    <>
      <PageHeader
        title="Tracking"
        description={`Where ${clientName}'s booked jobs came from, joined on phone and email.`}
        actions={headerActions}
      />
      <Tabs tabs={["results", "setup"]} value={tab} onChange={(v) => setTab(v === "setup" ? "setup" : "results")} />
    <div className="workflow-stack tracking-page">
      {setup.data && <ConnectionNotice clientId={clientId} feeds={feeds} compact />}

      {tab === "results" ? (<>
      {matchMutation.feedback}
      {/* ── Results: the four numbers, then where booked jobs came from ── */}
      {!shown && attribution.loading ? (
        <LoadingState label="Loading results" />
      ) : attribution.error && !shown ? (
        <ErrorState message={attribution.error} onRetry={attribution.reload} />
      ) : t ? (
        <div className={attributionBusy ? "tracking-busy" : undefined} aria-busy={attributionBusy}>
          <div className="module-metrics tracking-metrics">
            <MetricCard
              label="Matched jobs"
              value={t.jobs ? `${t.matched ?? 0} / ${t.jobs}` : "—"}
              detail={t.jobs ? `Matched / booked jobs ${phrase}` : t.job_export ? "No job export for these dates" : "No job export yet"}
            />
            <MetricCard
              label="Credited by Google"
              value={t.google_credited == null ? "—" : t.google_credited.toLocaleString()}
              detail={creditedDetail(t)}
            />
            <MetricCard
              label={wonDeals ? "Revenue: GHL won deals" : "Revenue from completed jobs"}
              value={t.revenue == null ? "—" : formatMoney(t.revenue, true)}
              detail={wonDeals
                ? `${t.won_deals ?? 0} won in GHL, no job export in this period`
                : t.revenue_source === "jobs"
                  ? `${plural(t.completed ?? 0, "completed job")}${partial && covers ? `, booked ${dayRange(covers)}` : ""}`
                  : t.job_export ? "No job export for these dates" : "No job export yet"}
            />
            <MetricCard
              label="Google spend"
              value={t.google_spend == null ? "—" : formatMoney(t.google_spend, true)}
              detail={t.google_spend == null
                ? "No Google Ads account linked"
                : t.roas != null
                  // The tile divides by every territory's spend (the same rule as the Ads report); By territory below
                  // divides each territory by its own, so a territory with spend but no jobs makes the two differ.
                  // When revenue covers only part of the period, ROAS divides by the spend on those days: say which.
                  ? `${partial && covers
                    ? `ROAS ${roasText(t.roas)} on ${t.roas_spend != null ? formatMoney(t.roas_spend, true) : "the"} spend ${dayRange(covers)}`
                    : `ROAS ${roasText(t.roas)}, all channels`}${(shown.by_location || []).filter((l) => Number(l.spend) > 0).length > 1 ? ", spend of every territory" : ""}`
                  : "Search, LSA and YouTube"}
            />
          </div>
          {exportStops && (
            <p className="inline-notice warning tracking-export-notice" role="status">
              <span>
                Job data stops {shortDate(exportStops.last_day)}
                {partial && covers ? `, so revenue and ROAS cover ${dayRange(covers)} only.` : ", so there is no revenue for these dates."}{" "}
                {canEdit && <button type="button" className="tracking-inline-link" onClick={goToUpload}>Upload a newer export</button>}
              </span>
            </p>
          )}
        </div>
      ) : null}

      <section className="panel" aria-labelledby="tracking-results-title">
        <div className="panel-heading">
          <div>
            <h2 id="tracking-results-title">Where booked jobs came from</h2>
          </div>
          {/* The window's own dates: today is included, and spend for today arrives with tonight's sync. */}
          {shown?.period && !(noResults && !t?.job_export) && <span className="panel-meta">{dayRange(shown.period)}</span>}
        </div>
        {!shown && attribution.loading ? (
          <LoadingState label="Loading results" />
        ) : attribution.error && !shown ? (
          <ErrorState message={attribution.error} onRetry={attribution.reload} />
        ) : shown && t ? (
          <div className={attributionBusy ? "tracking-busy" : undefined} aria-busy={attributionBusy}>
            {noResults ? (
              <div className="tracking-empty">
                <p>
                  {t.job_export
                    ? `No booked jobs ${phrase}: the job export stops ${shortDate(t.job_export.last_day)}. Upload a newer export, or pick a longer period.`
                    : "No job export yet. Upload one to see where booked jobs came from."}
                </p>
                {canEdit && <button type="button" className={t.job_export ? "button secondary" : "button"} onClick={goToUpload}>Go to the upload</button>}
              </div>
            ) : (
              <>
                <h3 className="tracking-subhead">By channel</h3>
                <DataTable
                  rows={shown.by_channel}
                  columns={["channel", "booked_jobs", "completed_jobs", "cancelled_jobs", "revenue"]}
                  empty={t.jobs
                    ? `None of these ${plural(t.jobs, "booked job")} matched a call or lead yet. Map every tracking number on the Setup tab; matching runs after each upload and every night.`
                    : "No booked jobs in these dates."}
                />
                <h3 className="tracking-subhead">
                  By campaign
                  <small>
                    {wonDeals ? "GHL won deals by the campaign on the contact's ad lead" : "Spend and cost per booked job"}
                    {shown.by_campaign.length > 0 && (shown.campaign_spend_span
                      ? !sameSpan(shown.campaign_spend_span, period) ? `. Spend and ROAS cover ${dayRange(shown.campaign_spend_span)}, the days with campaign spend${wonDeals ? "" : " and job data"}` : ""
                      : `. No campaign spend on the days the job export covers`)}
                  </small>
                </h3>
                <DataTable
                  rows={shown.by_campaign.map((c) => ({
                    ...c,
                    ...(wonDeals ? { revenue: wonRevenue(c.revenue) } : {}),
                    roas: c.roas == null ? null : roasText(Number(c.roas)),
                  }))}
                  columns={wonDeals
                    ? ["campaign", "won_deals", "revenue", "spend", "roas"]
                    : ["campaign", "booked_jobs", "completed_jobs", "revenue", "spend", "cost_per_booked_job", "roas"]}
                  empty={wonDeals
                    ? "No won deal traced to a campaign yet. A deal is credited when its GHL contact arrived with UTM tags or a Google campaign."
                    : "No campaign-level matches yet. They appear once matched calls and leads carry a campaign (tracking numbers with a campaign, or leads with UTM tags)."}
                />
                {(shown.by_location?.length ?? 0) > 1 || shown.by_location?.some((l) => l.credited_in_google != null) ? (
                  <>
                    <h3 className="tracking-subhead">By territory<small>Every job, Google spend, and what Google credited</small></h3>
                    <DataTable
                      rows={(shown.by_location || []).map((l) => ({
                        ...l,
                        ...(l.revenue_source === "ghl_won" && l.won_deals != null ? { revenue: wonRevenue(l.revenue) } : {}),
                        blended_roas: l.blended_roas == null ? null : roasText(Number(l.blended_roas)),
                      }))}
                      columns={wonDeals
                        ? ["location", "won_deals", "revenue", "spend", "credited_in_google", "cost_per_credited_booking", "blended_roas"]
                        : ["location", "booked_jobs", "completed_jobs", "revenue", "spend", "credited_in_google", "cost_per_credited_booking", "blended_roas"]}
                    />
                    <p className="workflow-note tracking-table-note">
                      {wonDeals
                        ? "No job export in this period, so revenue is the value of GHL opportunities marked won, on the territory with spend (Unassigned when there are several). Blended ROAS is all won value over Google spend, so it includes deals from other channels."
                        : "Every job in the territory counts, not only matched ones (National Accounts left out). Spend covers the whole period; Blended ROAS divides completed revenue by spend on the days the territory's job export covers, so it includes jobs from other channels."}
                    </p>
                  </>
                ) : null}
                <h3 className="tracking-subhead">Unmatched jobs<small>By what the customer said when asked how they heard</small></h3>
                <DataTable
                  rows={topWithRest(shown.unmatched_by_how_heard, 8)}
                  columns={["how_heard", "jobs", "revenue"]}
                  empty={t.jobs ? "Every job in this period matched a call or lead." : "No booked jobs in these dates."}
                />
                <h3 className="tracking-subhead">
                  Calls from Google Ads
                  <small>Through Google forwarding numbers{shown.detail_from ? `, recorded since ${shortDate(shown.detail_from)}` : ""}</small>
                </h3>
                <DataTable rows={shown.google_ads_calls} columns={["campaign", "calls", "calls_60s"]}
                  empty="Google reported no calls from ads in this period. Calls appear here when call assets use Google forwarding numbers." />
                <details className="tracking-notes-box">
                  <summary>How these numbers are counted</summary>
                  <ul className="tracking-notes">
                    {shown.notes.map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                </details>
              </>
            )}
          </div>
        ) : null}
      </section>
      </>) : (<>

      {/* ── Setup: where ads land, the checklist, and every feed that fills Results ── */}

      <section className="panel tracking-tier-panel" aria-label="Where this client's ads land">
        <RequestView request={setup}>
          {tierInfo && !changingTier ? (
            <div className="tracking-tier-line">
              <span className="tracking-tier-icon">{tierInfo.icon}</span>
              <p><strong>Tracking setup:</strong> {tierInfo.title}</p>
              {canEdit && <button type="button" className="button secondary" onClick={() => setChangingTier(true)}>Change</button>}
            </div>
          ) : (
            <>
              <div className="panel-heading">
                <div>
                  <h2>Where do this client's ads land?</h2>
                </div>
                {tierInfo && <button type="button" className="button secondary" onClick={() => setChangingTier(false)}>Cancel</button>}
              </div>
              <div className="tracking-tiers">
                {TIERS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`tracking-tier ${tier === o.value ? "active" : ""}`}
                    aria-pressed={tier === o.value}
                    disabled={!canEdit || tierMutation.busy}
                    onClick={() => void setTier(tier === o.value ? null : o.value)}
                  >
                    <span className="tracking-tier-icon">{o.icon}</span>
                    <span className="tracking-tier-body">
                      <strong>{o.title}</strong>
                      <span>{o.body}</span>
                    </span>
                    <span className="tracking-tier-mark" aria-hidden="true">{tier === o.value && <Check />}</span>
                  </button>
                ))}
              </div>
              {!tier && (
                <p className="workflow-note">
                  Pick one so the checklist shows the right steps. Calls and the job export work the same way for both.
                </p>
              )}
            </>
          )}
          {tierMutation.feedback}
        </RequestView>
      </section>

      <section className="panel" aria-labelledby="tracking-checklist-title">
        <div className="panel-heading">
          <div>
            <h2 id="tracking-checklist-title">Tracking checklist</h2>
          </div>
          <div className="tracking-heading-actions">
            {setup.data && (
              <div className="tracking-progress" role="img" aria-label={`${setup.data.progress.done} of ${setup.data.progress.total} steps done`}>
                <span className="tracking-progress-text">{setup.data.progress.done} of {setup.data.progress.total} done</span>
                <span className="tracking-progress-track">
                  <i style={{ width: `${setup.data.progress.total ? Math.round((setup.data.progress.done / setup.data.progress.total) * 100) : 0}%` }} />
                </span>
              </div>
            )}
            <button className="button secondary" onClick={setup.reload}>
              Refresh
            </button>
          </div>
        </div>
        {checkMutation.feedback}
        <RequestView request={setup}>
          {steps.some((s) => s.manual) && (
            <p className="tracking-legend">Steps with a box happen in Google Ads, GHL or with the client: tick them once done. The rest check themselves.</p>
          )}
          {groups.map((g) => {
            const groupSteps = steps.filter((s) => s.group === g);
            const required = groupSteps.filter((s) => !s.optional);
            const done = required.filter((s) => s.done).length;
            const complete = done === required.length && !groupSteps.some((s) => s.stale);
            // A finished group folds away so what is left to do is what you see.
            return (
              <details key={g} className="tracking-group" open={!complete}>
                <summary>
                  <h3>{g}</h3>
                  <span className={`tracking-group-count${complete ? " done" : ""}`}>
                    {complete && <Check aria-hidden="true" />}
                    {complete ? "All done" : `${done} of ${required.length} ${required.length < groupSteps.length ? "required " : ""}done`}
                  </span>
                </summary>
                <ul className="tracking-steps">
                  {groupSteps.map((s) => (
                    <li key={s.key} className={[s.done ? "done" : s.stale ? "stale" : "", s.manual ? "is-manual" : "is-auto"].filter(Boolean).join(" ")}>
                      {s.manual ? (
                        // A step a person does: a box to tick. The whole cell is the tap target, not just the 18px box.
                        <label className="tracking-step-check">
                          <input
                            type="checkbox"
                            aria-label={s.label}
                            checked={s.done}
                            disabled={!canEdit || checkMutation.busy}
                            onChange={(e) => void tick(s, e.target.checked)}
                          />
                        </label>
                      ) : (
                        // A step the app checks itself: no box, its state is the word at the end.
                        <span className="tracking-step-spacer" aria-hidden="true" />
                      )}
                      <div>
                        <strong>{s.label}</strong>
                        {(s.detail || s.link) && (
                          <span>
                            {stepDetail(s)}
                            {s.link && <>{s.detail ? (/[.!?:]$/.test(s.detail.trim()) ? " " : ". ") : ""}<Link to={s.link.to}>{s.link.label}</Link></>}
                          </span>
                        )}
                      </div>
                      {!s.manual && <StepState step={s} />}
                    </li>
                  ))}
                </ul>
              </details>
            );
          })}
        </RequestView>
      </section>

      <ConversionFeed clientId={clientId} onChanged={reloadResults} />
      <MetaConversions clientId={clientId} />

      <section className="panel" id="tracking-numbers" aria-labelledby="tracking-numbers-title">
        <div className="panel-heading">
          <div>
            <h2 id="tracking-numbers-title">Tracking numbers</h2>
          </div>
          {canEdit && !!conn?.ctm_accounts && (
            <button className="button secondary" disabled={importMutation.busy} onClick={() => void importCtm()}>
              {importMutation.busy ? "Importing…" : "Import from CTM"}
            </button>
          )}
        </div>
        {importMutation.feedback}
        {/* A stopped CTM feed is reported once, in the notice at the top of the page. */}
        <p className="workflow-note">
          Without a number swap on the site, a call is attributed by which
          number rang. Import every number from the client's CTM accounts and
          confirm its channel, or add numbers by hand: call assets, GBP, LSA,
          TV and radio, GHL pages.
        </p>
        <RequestView request={numbers}>
          <DataTable
            rows={(numbers.data?.numbers || []).map((n) => ({
              ...n, channel_key: n.channel, phone: formatPhone(n.phone_e164), channel: channels[n.channel] || n.channel,
              active: n.needs_channel ? "Imported: confirm channel" : n.active ? "Active" : "Retired",
            }))}
            // A status column only earns its place once something is retired or waiting.
            columns={["phone", "channel", "label", "campaign", "market_name", "calls_30d", ...((numbers.data?.numbers || []).some((n) => !n.active) ? ["active"] : [])]}
            empty={unmapped.length
              ? `No numbers mapped yet. CTM has seen ${unmapped.length === 1 ? "the number" : `the ${unmapped.length} numbers`} below${canEdit ? `: pick a channel and press Add so ${unmapped.length === 1 ? "its" : "their"} calls get credited.` : ", not credited to a channel yet."}`
              : canEdit
                ? conn?.ctm_accounts
                  ? "No tracking numbers yet. Press \"Import from CTM\" or add every number NOLA controls below so calls are credited to the channel that rang."
                  : "No tracking numbers yet. Add every number NOLA controls below so calls are credited to the channel that rang."
                : "No tracking numbers yet."}
            action={canEdit ? (row) => row.needs_channel ? (
              <span className="tracking-confirm">
                <select aria-label={`Channel for ${row.phone}`} value={confirmChannel[String(row.id)] || row.channel_key} onChange={(e) => setConfirmChannel((c) => ({ ...c, [String(row.id)]: e.target.value }))}>
                  {channelOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button className="button" disabled={numberMutation.busy} onClick={() => void confirmNumber(row)}>Confirm</button>
              </span>
            ) : row.active === "Active" ? (
              <button className="button secondary" disabled={numberMutation.busy} onClick={() => void removeNumber(row)}>Retire</button>
            ) : null : undefined}
          />
          {unmapped.length > 0 && (
            <>
              <h3 className="tracking-subhead">Unmapped CTM numbers<small>Seen by CTM, not credited to a channel yet</small></h3>
              <DataTable
                rows={unmapped.map((n) => ({ ...n, number: n.phone ? formatPhone(n.phone) : n.tracking_number }))}
                columns={["number", "label", "source", "calls", "last_call"]}
                action={canEdit ? (row) => row.phone ? (
                  <span className="tracking-confirm">
                    <select aria-label={`Channel for ${row.number}`} value={confirmChannel[`u:${row.tracking_number}`] || row.guessed_channel} onChange={(e) => setConfirmChannel((c) => ({ ...c, [`u:${row.tracking_number}`]: e.target.value }))}>
                      {channelOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button className="button" disabled={numberMutation.busy} onClick={() => void addUnmapped(row)}>Add</button>
                  </span>
                ) : null : undefined}
              />
            </>
          )}
        </RequestView>
        {numberMutation.feedback}
        {canEdit && numbers.data && (
          <div className={`workflow-panel inline-form ${territories.length > 1 ? "inline-form-5" : "inline-form-4"} tracking-add-number`}>
            <h3 className="inline-form-title">Add a number</h3>
            <ActionForm
              key={addKey}
              submit="Add number"
              busy={numberMutation.busy}
              onSubmit={addNumber}
              fields={[
                { name: "phone", label: "Phone number", required: true, placeholder: "(916) 555-0123" },
                // No channel is picked for you: a TV number saved as Google Ads would credit the wrong channel.
                { name: "channel", label: "Channel", required: true, options: [{ value: "", label: "Choose a channel" }, ...channelOptions] },
                ...(territories.length > 1
                  ? [{ name: "location_id", label: "Territory", options: [{ value: "", label: "Whole client" }, ...territories.map((l) => ({ value: String(l.id), label: l.market_name }))] }]
                  : []),
                // Placeholders read as examples, never as values someone already typed.
                { name: "label", label: "Label", placeholder: "e.g. Website call button" },
                { name: "campaign", label: "Campaign or creative", placeholder: "e.g. Spring TV spot" },
              ]}
            />
          </div>
        )}
      </section>

      {tier === "client_site" ? (
        // Scripts are not allowed on these clients' sites; the panel stays for any GHL page NOLA runs for them.
        <TrackingSites clientId={clientId} foldedTitle="Tracking script (only for GHL pages NOLA controls)" defaultOpen={location.hash === "#tracking-script"} />
      ) : (
        <TrackingSites clientId={clientId} />
      )}

      <section className="panel" id="tracking-leadforms" aria-labelledby="tracking-leadforms-title">
        <div className="panel-heading">
          <div>
            <h2 id="tracking-leadforms-title">Google Ads lead forms</h2>
          </div>
          {canEdit && (
            <button
              className="button secondary"
              disabled={!isAdmin || keyMutation.busy}
              title={isAdmin ? undefined : "Only an admin can do this"}
              onClick={() => void generateKey()}
            >
              {keyMutation.busy ? "Generating…" : keyLive ? "Replace webhook key" : "Generate webhook key"}
            </button>
          )}
        </div>
        <p className="workflow-note">
          The form sits inside the ad, so the lead never reaches the client's
          site and needs no script. Each lead lands here within five minutes
          and goes on the work queue for follow-up.
        </p>
        {keyMutation.feedback}
        {newKey && (
          <div className="tracking-site tracking-site-new tracking-key" role="status">
            <strong>Paste both into Google Ads now. The key is not shown again.</strong>
            <span className="tracking-key-label">Webhook URL</span>
            <div className="tracking-snippet">
              <code>{newKey.webhook_url}</code>
              <button className="button secondary" onClick={() => void copy(newKey.webhook_url, "Webhook URL")}>Copy</button>
            </div>
            <span className="tracking-key-label">Key</span>
            <div className="tracking-snippet">
              <code>{newKey.key}</code>
              <button className="button" onClick={() => void copy(newKey.key, "Key")}>Copy</button>
            </div>
            <p>{newKey.note}</p>
          </div>
        )}
        <h3 className="tracking-subhead">Recent form leads</h3>
        <RequestView request={leads}>
          <DataTable
            rows={(leads.data?.leads || []).slice(0, allLeads ? undefined : 10).map((l) => {
              const source = l.source === "google_lead_form" ? "Google lead form" : l.source === "site_form" ? "Tracking script" : "GHL contact";
              return {
                ...l,
                // Google's "Send test data" leads are kept apart from real ones everywhere else; say so here.
                source: l.is_test ? <span>{source}<em className="tracking-tag">Test</em></span> : source,
                has_gclid: l.has_gclid ? "Yes" : "No",
                submitted_at: formatDate(l.submitted_at),
                phone: formatPhone(l.phone_e164),
              };
            })}
            columns={["submitted_at", "source", "full_name", "phone", "email", "campaign_name", "has_gclid"]}
            empty="No form leads yet. They land here from Google Ads lead forms, the tracking script and GHL contacts."
          />
          {(leads.data?.leads.length ?? 0) > 10 && (
            <div className="tracking-more">
              <button className="button secondary" onClick={() => setAllLeads((v) => !v)}>
                {allLeads ? "Show the latest 10" : `Show all ${leads.data!.leads.length}`}
              </button>
            </div>
          )}
        </RequestView>
      </section>
      </>)}
    </div>
    </>
  );
}

export default function Tracking() {
  const { clientId, client } = useClientSelection();
  // A client's page draws its own header: the period and Rebuild matches sit in it.
  if (clientId) return <TrackingWorkspace key={clientId} clientId={clientId} clientName={client?.name || "this client"} />;
  return (
    <>
      <PageHeader
        title="Tracking"
        description="Every client's tracking at a glance. Pick a client to set it up or see its attribution."
      />
      <TrackingOverview />
    </>
  );
}
